import { createSlice, PayloadAction, createAction } from '@reduxjs/toolkit';
import { LearningProgressStats, LearningPath, WeeklyPlan, DailySchedule } from '@/services/learningProgress';

// Async actions for saga
export const fetchDashboardStats = createAction<{ userId?: number }>('learningProgress/fetchDashboardStats');
export const fetchDashboardStatsSuccess = createAction<LearningProgressStats>(
    'learningProgress/fetchDashboardStatsSuccess',
);
export const fetchDashboardStatsFailure = createAction<string>('learningProgress/fetchDashboardStatsFailure');

export const fetchCurrentWeekData = createAction('learningProgress/fetchCurrentWeekData');
export const fetchCurrentWeekDataSuccess = createAction<{ weekPlan: WeeklyPlan; dailySchedules: DailySchedule[] }>(
    'learningProgress/fetchCurrentWeekDataSuccess',
);
export const fetchCurrentWeekDataFailure = createAction<string>('learningProgress/fetchCurrentWeekDataFailure');

export const completeQuestion = createAction<{
    questionMappingId: string;
    userAnswer: string;
    timeSpentMinutes: number;
}>('learningProgress/completeQuestion');
export const completeQuestionSuccess = createAction<{ message: string }>('learningProgress/completeQuestionSuccess');
export const completeQuestionFailure = createAction<string>('learningProgress/completeQuestionFailure');

export const updateLessonProgress = createAction<{
    scheduleId: string;
    progressData: any;
}>('learningProgress/updateLessonProgress');
export const updateLessonProgressSuccess = createAction<{ message: string }>(
    'learningProgress/updateLessonProgressSuccess',
);
export const updateLessonProgressFailure = createAction<string>('learningProgress/updateLessonProgressFailure');

export interface LearningProgressState {
    dashboardStats: LearningProgressStats | null;
    currentWeekData: { weekPlan: WeeklyPlan; dailySchedules: DailySchedule[] } | null;
    isLoading: boolean;
    isUpdating: boolean;
    error: string | null;
    lastUpdated: string | null;
}

const initialState: LearningProgressState = {
    dashboardStats: null,
    currentWeekData: null,
    isLoading: false,
    isUpdating: false,
    error: null,
    lastUpdated: null,
};

const learningProgressSlice = createSlice({
    name: 'learningProgress',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        resetState: () => initialState,
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        setUpdating: (state, action: PayloadAction<boolean>) => {
            state.isUpdating = action.payload;
        },
    },
    extraReducers: (builder) => {
        // Fetch Dashboard Stats
        builder
            .addCase(fetchDashboardStats, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchDashboardStatsSuccess, (state, action) => {
                state.isLoading = false;
                state.dashboardStats = action.payload;
                state.lastUpdated = new Date().toISOString();
                state.error = null;
            })
            .addCase(fetchDashboardStatsFailure, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            });

        // Fetch Current Week Data
        builder
            .addCase(fetchCurrentWeekData, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchCurrentWeekDataSuccess, (state, action) => {
                state.isLoading = false;
                state.currentWeekData = action.payload;
                state.lastUpdated = new Date().toISOString();
                state.error = null;
            })
            .addCase(fetchCurrentWeekDataFailure, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            });

        // Complete Question
        builder
            .addCase(completeQuestion, (state) => {
                state.isUpdating = true;
                state.error = null;
            })
            .addCase(completeQuestionSuccess, (state) => {
                state.isUpdating = false;
                state.error = null;
                // Trigger refresh of dashboard stats
            })
            .addCase(completeQuestionFailure, (state, action) => {
                state.isUpdating = false;
                state.error = action.payload;
            });

        // Update Lesson Progress
        builder
            .addCase(updateLessonProgress, (state) => {
                state.isUpdating = true;
                state.error = null;
            })
            .addCase(updateLessonProgressSuccess, (state) => {
                state.isUpdating = false;
                state.error = null;
                // Trigger refresh of dashboard stats
            })
            .addCase(updateLessonProgressFailure, (state, action) => {
                state.isUpdating = false;
                state.error = action.payload;
            });
    },
});

export const { clearError, resetState, setLoading, setUpdating } = learningProgressSlice.actions;

export default learningProgressSlice.reducer;
